import { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { CreditCard, Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * DatafonosPanel — CRUD de datáfonos vinculados a un banco.
 * Se muestra dentro del detalle del banco (Tab "Datáfonos").
 */
export default function DatafonosPanel({ bankId, bankName, canEdit = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", brand_model: "", terminal_number: "", active: true });
  const [saving, setSaving] = useState(false);

  const fetchList = async () => {
    if (!bankId) return;
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/datafonos?bank_id=${bankId}`);
      setItems(r.data || []);
    } catch (err) {
      toast.error("No se pudo cargar los datáfonos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, [bankId]);

  const submitCreate = async () => {
    if (!form.name.trim()) { toast.error("Nombre requerido"); return; }
    setSaving(true);
    try {
      await axiosInstance.post(`/datafonos`, {
        name: form.name.trim(),
        bank_id: bankId,
        brand_model: form.brand_model.trim() || null,
        terminal_number: form.terminal_number.trim() || null,
        active: !!form.active,
      });
      toast.success("Datáfono creado");
      setForm({ name: "", brand_model: "", terminal_number: "", active: true });
      setShowForm(false);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (d) => {
    try {
      await axiosInstance.patch(`/datafonos/${d.id}`, { active: !d.active });
      fetchList();
    } catch (err) {
      toast.error("No se pudo actualizar");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`¿Eliminar datáfono "${d.name}"?`)) return;
    try {
      await axiosInstance.delete(`/datafonos/${d.id}`);
      toast.success("Eliminado");
      fetchList();
    } catch (err) {
      toast.error("No se pudo eliminar");
    }
  };

  const updateField = async (d, field, value) => {
    try {
      await axiosInstance.patch(`/datafonos/${d.id}`, { [field]: value });
      fetchList();
    } catch (err) {
      toast.error("No se pudo actualizar");
    }
  };

  return (
    <div className="space-y-3" data-testid="datafonos-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-slate-600" />
          <h4 className="font-semibold text-sm">Datáfonos vinculados a {bankName}</h4>
          <span className="text-xs text-slate-500">({items.length})</span>
        </div>
        {canEdit && !showForm && (
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="add-datafono-btn">
            <Plus className="w-4 h-4 mr-1" />
            Agregar
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="border-blue-200 bg-blue-50/40">
          <CardContent className="pt-3 pb-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Nombre *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ej: Datáfono BAC principal" className="h-8" data-testid="new-datafono-name" />
              </div>
              <div>
                <Label className="text-xs">Marca / Modelo</Label>
                <Input value={form.brand_model} onChange={(e) => setForm({ ...form, brand_model: e.target.value })} placeholder="Ej: LANE/3000" className="h-8" data-testid="new-datafono-model" />
              </div>
              <div>
                <Label className="text-xs">Nº Terminal</Label>
                <Input value={form.terminal_number} onChange={(e) => setForm({ ...form, terminal_number: e.target.value })} placeholder="Ej: 12345678" className="h-8" data-testid="new-datafono-terminal" />
              </div>
              <div className="flex items-end gap-2">
                <div className="flex items-center gap-2">
                  <Switch id="new-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                  <Label htmlFor="new-active" className="text-xs">Activo</Label>
                </div>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button size="sm" onClick={submitCreate} disabled={saving} data-testid="save-datafono-btn">
                <Save className="w-4 h-4 mr-1" />
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {loading && <p className="text-sm text-slate-500 text-center py-2">Cargando...</p>}
      {!loading && items.length === 0 && (
        <p className="text-sm text-slate-500 text-center py-3 border rounded bg-slate-50">
          Sin datáfonos configurados. Presiona &quot;Agregar&quot; para vincular uno.
        </p>
      )}

      {items.map((d) => (
        <Card key={d.id} className={d.active ? "" : "opacity-60"} data-testid={`datafono-${d.id}`}>
          <CardContent className="pt-3 pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="grid grid-cols-3 gap-2 flex-1 min-w-0">
                <div className="min-w-0">
                  <Label className="text-[10px] text-slate-500">Nombre</Label>
                  <Input defaultValue={d.name} onBlur={(e) => e.target.value !== d.name && updateField(d, "name", e.target.value)} className="h-7" data-testid={`datafono-name-${d.id}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Marca/Modelo</Label>
                  <Input defaultValue={d.brand_model || ""} onBlur={(e) => e.target.value !== (d.brand_model || "") && updateField(d, "brand_model", e.target.value || null)} className="h-7" placeholder="—" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-500">Nº Terminal</Label>
                  <Input defaultValue={d.terminal_number || ""} onBlur={(e) => e.target.value !== (d.terminal_number || "") && updateField(d, "terminal_number", e.target.value || null)} className="h-7" placeholder="—" />
                </div>
              </div>
              <div className="flex flex-col items-end gap-2 shrink-0">
                <div className="flex items-center gap-1.5">
                  <Switch checked={!!d.active} onCheckedChange={() => toggleActive(d)} disabled={!canEdit} data-testid={`datafono-active-${d.id}`} />
                  <span className="text-xs text-slate-600">{d.active ? "Activo" : "Inactivo"}</span>
                </div>
                {canEdit && (
                  <Button size="icon" variant="ghost" onClick={() => remove(d)} className="h-7 w-7 text-red-600 hover:bg-red-50" title="Eliminar" data-testid={`delete-datafono-${d.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
